import { getConfigAreaId } from '@yard/utils/yard-block.util'
import { useCallback, useMemo } from 'react'
import { Node } from 'reactflow'
import { YardBlockWithOccupanciesAndActions } from '../models/yard-view.model'
import { YardBlockViewStore } from '../stores/nodes/yard-block.view-store'
import { YardViewStore } from '../stores/yard.view-store'

const FOCUSED_Z_INDEX = 1000

export const useYardBlockNodes = (
  viewStore: YardViewStore,
  yardBlockViewStore: YardBlockViewStore,
): Node<YardBlockWithOccupanciesAndActions>[] => {
  const onDoubleClick = useCallback(
    (blockId: string, bay?: number, row?: number) => {
      viewStore.setFocusedBlockId(blockId)
      viewStore.setContainerDetailsOpen(true, bay, row)
    },
    [viewStore],
  )

  return useMemo<Node<YardBlockWithOccupanciesAndActions>[]>(
    () =>
      viewStore.yardBlocks.map(block => ({
        id: block.id,
        type: 'yardBlock',
        ...(viewStore.focusedBlockId === block.id && { zIndex: FOCUSED_Z_INDEX }),
        position: {
          x: block.layoutPosition.x,
          y: block.layoutPosition.y,
        },
        data: {
          ...block,
          isEditable: !viewStore.isViewMode,
          showAllocationRulesPreview: viewStore.showAllocationRulesPreview,
          showNodeHeaders: viewStore.showNodeHeaders,
          colorScheme: viewStore.colorScheme,
          selectedSchemeValues: viewStore.effectiveSelectedValuesForActiveScheme,
          focused: viewStore.focusedBlockId === block.id,
          onEdit: yardBlockViewStore.openFormDialog,
          onFocus: viewStore.setFocusedBlockId,
          onRotate: yardBlockViewStore.setRotation,
          onDelete: yardBlockViewStore.setDeleteDialogOpen,
          onRenameBay: yardBlockViewStore.openRenameBayDialog,
          onRenameRow: yardBlockViewStore.openRenameRowDialog,
          onInsertBayLeft: async (blockId: string, bayIndex: number) =>
            await yardBlockViewStore.insertBayLeft(blockId, bayIndex),
          onInsertBayRight: async (blockId: string, bayIndex: number) =>
            await yardBlockViewStore.insertBayRight(blockId, bayIndex),
          onInsertRowUp: async (blockId: string, rowIndex: number) =>
            await yardBlockViewStore.insertRowUp(blockId, rowIndex),
          onInsertRowDown: async (blockId: string, rowIndex: number) =>
            await yardBlockViewStore.insertRowDown(blockId, rowIndex),
          onDeleteBay: async (blockId: string, bayIndex: number) =>
            await yardBlockViewStore.deleteBay(blockId, bayIndex),
          onDeleteRow: async (blockId: string, rowIndex: number) =>
            await yardBlockViewStore.deleteRow(blockId, rowIndex),
          onBlockConfigArea: async (blockId, minBay, maxBay, minRow, maxRow, description) =>
            await yardBlockViewStore.blockConfigArea(
              blockId,
              minBay,
              maxBay,
              minRow,
              maxRow,
              description,
            ),
          onDeleteConfigArea: async (
            configAreaType,
            minBay,
            maxBay,
            minRow,
            maxRow,
            reeferPlugDirection,
          ) => {
            const configId = getConfigAreaId(
              configAreaType,
              block.configAreas,
              minBay,
              maxBay,
              minRow,
              maxRow,
              reeferPlugDirection,
            )
            if (!configId) return
            await yardBlockViewStore.deleteConfigArea(configId, block.id)
          },
          onAddReeferPlugs: async (blockId: string, bay: number, direction) =>
            await yardBlockViewStore.reeferPlugConfigArea(blockId, bay, direction),
          onAddSpaceBetweenBays: (blockId: string, bay: number) =>
            yardBlockViewStore.openSpaceBetweenBaysDialog(blockId, bay),
          onDeleteSpaceBetweenBays: async (blockId: string, bay: number) =>
            await yardBlockViewStore.deleteSpaceBetweenBaysConfigArea(blockId, bay),
          onAdjustMaxTier: yardBlockViewStore.openLimitStackHeightDialog,
          onAddAccessPoint: yardBlockViewStore.accessPoints.openAddAccessPointDialog,
          onEditAccessPoint: yardBlockViewStore.accessPoints.openEditAccessPointDialog,
          onDoubleClick,
        },
      })),
    [
      viewStore.yardBlocks,
      viewStore.focusedBlockId,
      viewStore.isViewMode,
      viewStore.showAllocationRulesPreview,
      viewStore.showNodeHeaders,
      viewStore.colorScheme,
      viewStore.effectiveSelectedValuesForActiveScheme,
      viewStore.setFocusedBlockId,
      yardBlockViewStore,
      onDoubleClick,
    ],
  )
}
